import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../components/Topbar";
import { archiveCompany, restoreCompany, listCompanies, deleteCompany } from "../api/companies";
import { SCompanyRow, SCompanyOut } from "../types/companies";
import FilterBar from "../components/controls/FilterBar";
import SearchInput from "../components/controls/SearchInput";
import { Archive, RotateCcw, CheckCircle2, X, Users, BedDouble, Pencil, Trash2, MapPin, Building2, LayoutGrid, Table, UsersRound, CalendarDays, ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import AddCompanyModal from "../components/modals/AddCompanyModal";
import EditCompanyModal from "../components/modals/EditCompanyModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import { useDebouncedUrlQuery } from "../hooks/useDebouncedUrlQuery";

type SortField = "id" | "company_name" | "subscription_expires_on";
type SortDirection = "asc" | "desc";

const SORT_LABELS: Record<SortField, string> = {
  id: "ID",
  company_name: "Компания",
  subscription_expires_on: "Подписка",
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU");
}

export default function CompaniesPage() {
  const [viewMode, setViewMode] = useState<"cards" | "table">(() => {
    if (typeof window === "undefined") return "cards";
    const saved = window.localStorage.getItem("companies:view");
    return saved === "table" ? "table" : "cards";
  });
  const [items, setItems] = useState<SCompanyRow[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [editingCompany, setEditingCompany] = useState<SCompanyOut | null>(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletingCompanyId, setDeletingCompanyId] = useState<number | null>(null);
  const [openArchive, setOpenArchive] = useState(false);
  const [archivingCompany, setArchivingCompany] = useState<{ id: number; name: string } | null>(null);
  const [openRestore, setOpenRestore] = useState(false);
  const [restoringCompany, setRestoringCompany] = useState<{ id: number; name: string } | null>(null);
  const [actionSuccess, setActionSuccess] = useState("");
  const [hideExpiredDraft, setHideExpiredDraft] = useState(false);
  const [hideExpiredFilter, setHideExpiredFilter] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [orderBy, setOrderBy] = useState<SortField>("id");
  const [orderDir, setOrderDir] = useState<SortDirection>("desc");
  const [searchDraft, setSearchDraft, searchQuery] = useDebouncedUrlQuery();
  const requestIdRef = useRef(0);

  const reload = useCallback(async (targetPage: number = page) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await listCompanies({
        page: targetPage,
        per_page: perPage,
        q: searchQuery || null,
        order_by: orderBy,
        order_dir: orderDir,
        hide_expired_subscription: hideExpiredFilter,
        show_archived: showArchived,
      });
      if (requestId !== requestIdRef.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      if (requestId !== requestIdRef.current) return;
      setError(e?.response?.data?.detail || "Ошибка загрузки компаний");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [hideExpiredFilter, orderBy, orderDir, perPage, page, searchQuery, showArchived]);

  useEffect(() => {
    reload(page);
  }, [page, perPage, reload]);

  useEffect(() => {
    window.localStorage.setItem("companies:view", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!actionSuccess) return;
    const timer = window.setTimeout(() => setActionSuccess(""), 4000);
    return () => window.clearTimeout(timer);
  }, [actionSuccess]);

  const pages = Math.max(1, Math.ceil(total / perPage));
  const hasActiveFilters = hideExpiredFilter || showArchived;
  const stats = useMemo(
    () =>
      [
        { label: "Всего компаний", value: total.toLocaleString("ru-RU"), desc: "в базе TravelStat" },
        {
          label: "Сотрудников (страница)",
          value: items.reduce((sum, c) => sum + (c.employees_count || 0), 0).toLocaleString("ru-RU"),
          desc: "сумма по текущему списку",
        },
        {
          label: "Объектов (страница)",
          value: items.reduce((sum, c) => sum + (c.objects_count || 0), 0).toLocaleString("ru-RU"),
          desc: "активные объекты компаний",
        },
        {
          label: "Лимит объектов",
          value: items.length
            ? Math.round(items.reduce((sum, c) => sum + (c.objects_limit || 0), 0) / items.length).toLocaleString("ru-RU")
            : "—",
          desc: "средний по текущему списку",
        },
        {
          label: "Лимит конкурентов",
          value: items.length
            ? Math.round(items.reduce((sum, c) => sum + (c.competitors_count ?? 5), 0) / items.length).toLocaleString("ru-RU")
            : "—",
          desc: "средний на объект в списке",
        },
      ] as { label: string; value: string; desc: string }[],
    [items, total]
  );

  function startEdit(company: SCompanyOut) {
    setEditingCompany(company);
    setOpenEdit(true);
  }

  function startDelete(id: number) {
    setDeletingCompanyId(id);
    setOpenDelete(true);
  }

  function startArchive(company: SCompanyRow) {
    setArchivingCompany({ id: company.id, name: company.company_name });
    setOpenArchive(true);
  }

  function startRestore(company: SCompanyRow) {
    setRestoringCompany({ id: company.id, name: company.company_name });
    setOpenRestore(true);
  }

  function applyFilters() {
    setHideExpiredFilter(hideExpiredDraft);
    setPage(1);
  }

  function resetFilters() {
    setHideExpiredDraft(false);
    setHideExpiredFilter(false);
    setShowArchived(false);
    setPage(1);
  }

  function toggleSort(field: SortField) {
    setPage(1);
    if (orderBy === field) {
      setOrderDir(orderDir === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(field);
      setOrderDir("asc");
    }
  }

  function renderSortIcon(field: SortField) {
    if (orderBy !== field) {
      return <ChevronsUpDown size={16} className="text-slate-400" />;
    }
    return orderDir === "asc" ? (
      <ChevronUp size={16} className="text-white" />
    ) : (
      <ChevronDown size={16} className="text-white" />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />
      {actionSuccess && (
        <div
          role="status"
          aria-live="polite"
          className="fixed right-4 top-20 z-40 flex max-w-md items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900 shadow-[0_14px_35px_rgba(16,185,129,0.2)]"
        >
          <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
          <span className="text-sm font-medium">{actionSuccess}</span>
          <button
            type="button"
            onClick={() => setActionSuccess("")}
            className="ml-1 rounded-full p-0.5 text-emerald-700 transition hover:bg-emerald-100"
            aria-label="Закрыть уведомление"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <main className="mx-auto w-full max-w-[1200px] px-6 py-10 space-y-8">
        <section className="rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Компании</h1>
              <p className="mt-2 text-sm text-slate-500">
                Управляйте компаниями, следите за количеством сотрудников и объектов, создавайте новых партнеров.
              </p>
            </div>
            <button
              onClick={() => setOpenAdd(true)}
              className="inline-flex items-center gap-2 self-start rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_15px_30px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105"
            >
              <span className="text-lg leading-none">+</span>
              Добавить компанию
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {stats.map((stat, idx) => (
            <div key={idx} className="rounded-3xl border border-white/70 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
              <p className="text-xs uppercase tracking-wide text-slate-400">{stat.label}</p>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{stat.value}</div>
              <p className="mt-1 text-sm text-slate-500">{stat.desc}</p>
            </div>
          ))}
        </section>

        <section className="space-y-4">
          <FilterBar
            onApply={applyFilters}
            onReset={resetFilters}
            hasActive={hasActiveFilters}
            applyDisabled={loading}
            sticky={false}
            noWrap
          >
            <label className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={hideExpiredDraft}
                onChange={(event) => setHideExpiredDraft(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Скрыть с истекшей подпиской
            </label>
            <label className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => {
                  setShowArchived(event.target.checked);
                  setPage(1);
                }}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Отобразить архивные
            </label>
          </FilterBar>

          <section className="grid gap-3 rounded-3xl border border-indigo-100 bg-gradient-to-r from-white via-indigo-50/70 to-white p-4 shadow-[0_12px_30px_rgba(99,102,241,0.08)] md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Поиск компаний</p>
              <SearchInput
                value={searchDraft}
                onChange={(value) => {
                  setSearchDraft(value);
                  setPage(1);
                }}
                placeholder="Поиск по названию или ИНН"
                variant="soft"
                className="mt-2"
              />
            </div>
            <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm text-slate-500 shadow-sm md:text-right">
              <div className="text-xs uppercase tracking-wide text-slate-400">В выдаче</div>
              <div className="mt-1 text-xl font-semibold text-slate-900">{total.toLocaleString("ru-RU")}</div>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-white/70 bg-white/90 p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-500">Сортировка:</span>
              {(Object.keys(SORT_LABELS) as SortField[]).map((field) => {
                const isActive = orderBy === field;
                return (
                  <button
                    key={field}
                    type="button"
                    onClick={() => toggleSort(field)}
                    className={[
                      "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition",
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    {SORT_LABELS[field]}
                    <span className={isActive ? "text-white" : ""}>{renderSortIcon(field)}</span>
                  </button>
                );
              })}
            </div>
            <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                  viewMode === "cards" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <LayoutGrid size={16} />
                Плашки
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                  viewMode === "table" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Table size={16} />
                Таблица
              </button>
            </div>
          </div>
          {loading &&
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="h-32 rounded-3xl border border-white/70 bg-white/60 shadow-inner animate-pulse" />
            ))}

          {!loading && items.length === 0 && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-12 text-center text-slate-500">
              {searchQuery ? "Ничего не найдено" : "Компаний пока нет. Добавьте первую, чтобы начать работу."}
            </div>
          )}

          {!loading && items.length > 0 && viewMode === "cards" && (
            <>
              {items.map(c => (
                <div
                  key={c.id}
                  className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_12px_32px_rgba(15,23,42,0.08)] transition hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(99,102,241,0.18)]"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                          <Building2 size={20} />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-lg font-semibold text-slate-900">{c.company_name}</p>
                            {c.archived_at && (
                              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                                В архиве
                              </span>
                            )}
                          </div>
                          <p className="text-xs uppercase tracking-wider text-slate-400">ID {c.id}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100/80 px-3 py-1">
                          ИНН: {c.inn || "—"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100/80 px-3 py-1">
                          <MapPin size={14} className="text-slate-500" />
                          {c.city || "Не указан"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100/80 px-3 py-1">
                          <CalendarDays size={14} className="text-slate-500" />
                          Подписка до {formatDate(c.subscription_expires_on)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm font-semibold text-slate-700">
                      <span className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2">
                        <Users size={16} className="text-indigo-500" /> {c.employees_count ?? 0} сотрудников
                      </span>
                      <span className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2">
                        <BedDouble size={16} className="text-indigo-500" /> {c.objects_count ?? 0} / {c.objects_limit ?? 0} объектов
                      </span>
                      <span className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2">
                        <UsersRound size={16} className="text-indigo-500" /> {c.competitors_count ?? 5} конкурентов / объект
                      </span>
                      <span className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-2">
                        <LayoutGrid size={16} className="text-indigo-500" /> {c.ota_count ?? 3} ОТА по умолчанию
                      </span>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <button
                        className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                        onClick={() => startEdit(c)}
                      >
                        <Pencil size={18} />
                      </button>
                      {c.archived_at ? (
                        <button
                          className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50"
                          onClick={() => startRestore(c)}
                          title="Восстановить из архива"
                          aria-label="Восстановить из архива"
                        >
                          <RotateCcw size={18} />
                        </button>
                      ) : (
                        <button
                          className="inline-flex items-center justify-center rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm font-medium text-amber-700 transition hover:bg-amber-50"
                          onClick={() => startArchive(c)}
                          title="Добавить в архив"
                          aria-label="Добавить в архив"
                        >
                          <Archive size={18} />
                        </button>
                      )}
                      <button
                        className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50"
                        onClick={() => startDelete(c.id)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {!loading && items.length > 0 && viewMode === "table" && (
            <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_12px_32px_rgba(15,23,42,0.08)]">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-5 py-4">Компания</th>
                      <th className="px-5 py-4">ИНН</th>
                      <th className="px-5 py-4">Город</th>
                      <th className="px-5 py-4">Сотрудники</th>
                      <th className="px-5 py-4">Объекты</th>
                      <th className="px-5 py-4">Лимит объектов</th>
                      <th className="px-5 py-4">Конкурентов / объект</th>
                      <th className="px-5 py-4">Кол-во ОТА по умолчанию</th>
                      <th className="px-5 py-4">Подписка до</th>
                      <th className="px-5 py-4 text-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map(c => (
                      <tr key={c.id} className="transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                              <Building2 size={18} />
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="font-semibold text-slate-900">{c.company_name}</div>
                                {c.archived_at && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                    В архиве
                                  </span>
                                )}
                              </div>
                              <div className="text-xs uppercase tracking-wide text-slate-400">ID {c.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">{c.inn || "—"}</td>
                        <td className="px-5 py-4">{c.city || "Не указан"}</td>
                        <td className="px-5 py-4">{c.employees_count ?? 0}</td>
                        <td className="px-5 py-4">{c.objects_count ?? 0}</td>
                        <td className="px-5 py-4">{c.objects_limit ?? 0}</td>
                        <td className="px-5 py-4">{c.competitors_count ?? 5}</td>
                        <td className="px-5 py-4">{c.ota_count ?? 3}</td>
                        <td className="px-5 py-4">
                          <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <CalendarDays size={16} className="text-slate-500" />
                            {formatDate(c.subscription_expires_on)}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                              onClick={() => startEdit(c)}
                            >
                              <Pencil size={16} />
                            </button>
                            {c.archived_at ? (
                              <button
                                className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50"
                                onClick={() => startRestore(c)}
                                title="Восстановить из архива"
                                aria-label="Восстановить из архива"
                              >
                                <RotateCcw size={16} />
                              </button>
                            ) : (
                              <button
                                className="inline-flex items-center justify-center rounded-2xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-50"
                                onClick={() => startArchive(c)}
                                title="Добавить в архив"
                                aria-label="Добавить в архив"
                              >
                                <Archive size={16} />
                              </button>
                            )}
                            <button
                              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
                              onClick={() => startDelete(c.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section className="flex flex-wrap items-center gap-4 rounded-3xl border border-white/70 bg-white/80 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">На странице:</label>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100].map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 md:ml-auto">
            <button
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Назад
            </button>
            <div className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
              {page} / {pages}
            </div>
            <button
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page >= pages || loading}
            >
              Вперёд
            </button>
          </div>
        </section>

        {error && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
            {error}
          </div>
        )}

        <AddCompanyModal
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onCreated={() => {
            setOpenAdd(false);
            setPage(1);
            reload(1);
          }}
        />
        <EditCompanyModal
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          company={editingCompany}
          onUpdated={() => reload(page)}
        />
        <ConfirmDeleteModal
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          onConfirm={async () => {
            if (deletingCompanyId) {
              await deleteCompany(deletingCompanyId);
              reload(page);
            }
          }}
          title="Удаление компании"
          message="Вы уверены, что хотите удалить компанию?"
        />
        <ConfirmDeleteModal
          open={openArchive}
          onClose={() => setOpenArchive(false)}
          onConfirm={async () => {
            if (archivingCompany) {
              await archiveCompany(archivingCompany.id);
              await reload(page);
              setActionSuccess(`Компания «${archivingCompany.name}» добавлена в архив`);
            }
          }}
          action="archive"
          title="Добавление компании в архив"
          message="Вы уверены, что хотите добавить в архив?"
          confirmLabel="Да"
          loadingLabel="Добавляем…"
          cancelLabel="Нет"
          errorMessage="Ошибка при добавлении компании в архив"
        />
        <ConfirmDeleteModal
          open={openRestore}
          onClose={() => setOpenRestore(false)}
          onConfirm={async () => {
            if (restoringCompany) {
              await restoreCompany(restoringCompany.id);
              await reload(page);
              setActionSuccess(`Компания «${restoringCompany.name}» восстановлена из архива`);
            }
          }}
          action="restore"
          title="Восстановление компании"
          message="Вы уверены, что хотите восстановить компанию из архива?"
          confirmLabel="Да"
          loadingLabel="Восстанавливаем…"
          cancelLabel="Нет"
          errorMessage="Ошибка при восстановлении компании из архива"
        />
      </main>
    </div>
  );
}
